import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeMouseHandler,
  type NodeTypes,
  type FitViewOptions,
} from '@xyflow/react';
import type { ZipTie } from '@erp/workflows';
import '@xyflow/react/dist/style.css';
import { MapTrifoldIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { StepNode } from 'src/lib/steps/step-node';
import { StepLayoutGroupNode } from 'src/pages/authenticated/workflows/components/StepLayoutGroupNode';
import { STEP_LAYOUT_GROUP_NODE_TYPE } from 'src/lib/workflows/stepLayoutGroups';
import { cn } from 'src/lib/utils';
import {
  GraphNestingDepthContext,
  useGraphNestingDepth,
} from './graphNestingDepth';
import { ExpressionNode } from 'src/pages/authenticated/workflows/components/ExpressionNode';
import {
  EXPRESSION_NODE_TYPE,
  withExpressionOverlay,
} from 'src/lib/workflows/expressionOverlay';
import { ZipTieOverlay } from 'src/pages/authenticated/workflows/components/ZipTieOverlay';
import { useViewZipTieState } from './viewZipTies';
import { ZipTieEdge } from './zipTieEdge';
import { ZipTieContext } from './zipTieRouting';

/** Read-only: stored `deletable` edges render without a delete button. */
const edgeTypes: EdgeTypes = { default: ZipTieEdge, deletable: ZipTieEdge };

function ignoreZipTieEdit() {}

const NO_CUT_TIE_IDS: ReadonlySet<string> = new Set();

const nodeTypes: NodeTypes = {
  stepNode: StepNode,
  // Groups render here too, so a run page or version preview shows the same
  // organisation the author saw. No rename/recolour callbacks are injected, so
  // the box is inert.
  [STEP_LAYOUT_GROUP_NODE_TYPE]: StepLayoutGroupNode,
  [EXPRESSION_NODE_TYPE]: ExpressionNode,
};

export interface WorkflowGraphViewProps {
  nodes: Node[];
  edges: Edge[];
  /** Zoom/pan controls, with a minimap toggle button when `showMiniMap`. */
  showControls?: boolean;
  /** Adds the minimap toggle to the controls (off until the user opens it). */
  showMiniMap?: boolean;
  /** The dotted canvas backdrop. */
  showBackground?: boolean;
  /** Allow panning and zooming. Off for decorative/embedded renders. */
  interactive?: boolean;
  /** Count this render as nested — suppresses recursive sub-action previews. */
  nested?: boolean;
  onNodeClick?: NodeMouseHandler;
  onPaneClick?: () => void;
  onNodeMouseEnter?: NodeMouseHandler;
  onNodeMouseLeave?: NodeMouseHandler;
  fitViewOptions?: FitViewOptions;
  minZoom?: number;
  maxZoom?: number;
  className?: string;
  /** Saved straps. Drawn read-only; gestures stay in the editor. */
  zipTies?: readonly ZipTie[];
  /** Rendered inside the flow — node toolbars, panels, overlays. */
  children?: ReactNode;
}

/**
 * Read-only renderer for a workflow graph.
 *
 * The shared view-only counterpart to `WorkflowEditor`: same StepNode fidelity,
 * but nothing is draggable, connectable, or selectable. Every piece of chrome
 * (controls, minimap, backdrop) is opt-in so the same component serves a
 * full-page overview, a dialog preview, and a hover preview beside a node.
 */
export function WorkflowGraphView({
  nodes,
  edges,
  showControls = false,
  showMiniMap = false,
  showBackground = false,
  interactive = false,
  nested = false,
  onNodeClick,
  onPaneClick,
  onNodeMouseEnter,
  onNodeMouseLeave,
  fitViewOptions = { maxZoom: 1, padding: 0.2 },
  minZoom = 0.2,
  maxZoom = 2,
  className,
  zipTies,
  children,
}: WorkflowGraphViewProps) {
  const gt = useGT();
  const depth = useGraphNestingDepth();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isMinimapOpen, setIsMinimapOpen] = useState(false);
  const zipTieView = useViewZipTieState(zipTies);

  // Manually overridden socket expressions are drawn the same way here as in
  // the editor, so a run page or preview shows the data path the engine
  // actually took — not the stored edge the expression replaced.
  const overlaid = useMemo(
    () => withExpressionOverlay(nodes, edges),
    [nodes, edges],
  );

  return (
    <GraphNestingDepthContext.Provider value={nested ? depth + 1 : depth}>
      <ReactFlowProvider>
        <div ref={canvasRef} className={cn('h-full w-full', className)}>
          <ZipTieContext.Provider value={zipTieView.renderState}>
            <ReactFlow
              nodes={overlaid.nodes}
              edges={overlaid.edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              fitViewOptions={fitViewOptions}
              minZoom={minZoom}
              maxZoom={maxZoom}
              attributionPosition="bottom-left"
              proOptions={{ hideAttribution: true }}
              defaultEdgeOptions={{ animated: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              nodesFocusable={interactive}
              edgesFocusable={interactive}
              disableKeyboardA11y={!interactive}
              panOnDrag={interactive}
              panOnScroll={false}
              zoomOnScroll={interactive}
              zoomOnPinch={interactive}
              zoomOnDoubleClick={interactive}
              preventScrolling={interactive}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onNodeMouseEnter={onNodeMouseEnter}
              onNodeMouseLeave={onNodeMouseLeave}
            >
              {showBackground && <Background />}
              {showControls && (
                <Controls>
                  {showMiniMap && (
                    <ControlButton
                      onClick={() => setIsMinimapOpen((v) => !v)}
                      title={
                        isMinimapOpen
                          ? gt('Hide minimap', {
                              context:
                                'tooltip for the hide minimap control button in workflow graph',
                            })
                          : gt('Show minimap', {
                              context:
                                'tooltip for the show minimap control button in workflow graph',
                            })
                      }
                    >
                      <MapTrifoldIcon />
                    </ControlButton>
                  )}
                </Controls>
              )}
              {showMiniMap && isMinimapOpen && <MiniMap />}
              {zipTieView.renderState && (
                <ZipTieOverlay
                  ties={zipTieView.renderState.ties}
                  edgeCount={overlaid.edges.length}
                  readOnly
                  canvasRef={canvasRef}
                  endpoints={zipTieView.endpointsRef}
                  previewCutTieIds={NO_CUT_TIE_IDS}
                  hoveredTieId={zipTieView.hoveredTieId}
                  onHover={zipTieView.setHoveredTieId}
                  onPreview={ignoreZipTieEdit}
                  onGesture={ignoreZipTieEdit}
                  onCommit={ignoreZipTieEdit}
                  onMenu={ignoreZipTieEdit}
                />
              )}
              {children}
            </ReactFlow>
          </ZipTieContext.Provider>
        </div>
      </ReactFlowProvider>
    </GraphNestingDepthContext.Provider>
  );
}
